import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const TREND = {
  naik: { label: "Tren naik", cls: "bg-emerald-50 text-emerald-700", Icon: TrendingUp },
  turun: { label: "Tren menurun", cls: "bg-rose-50 text-rose-700", Icon: TrendingDown },
  stabil: { label: "Tren stabil", cls: "bg-slate-100 text-slate-600", Icon: Minus },
};

const Dot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  const low = payload.score < 75;
  return <circle cx={cx} cy={cy} r={5} fill={low ? "#f97316" : "#0d9488"} stroke="#fff" strokeWidth={2} />;
};

const TrendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-100 px-3 py-2 text-xs">
      <p className="font-semibold text-slate-800">{p.title}</p>
      <p className="text-slate-500">{p.subject} · {p.date}</p>
      <p className={`font-display text-lg font-bold mt-1 ${p.score >= 75 ? "text-teal-600" : "text-orange-500"}`}>{p.score}</p>
    </div>
  );
};

export const GradeTrendChart = ({ data, emptyText = "Belum ada nilai final untuk ditampilkan.", testId = "grade-trend" }) => {
  if (!data) return null;
  const pts = (data.points || []).map((p, i) => ({ ...p, idx: i + 1 }));
  const t = data.trend ? TREND[data.trend] : null;
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-2 mb-4">
        <Stat label="Rata-rata" value={data.average ?? "—"} testId={`${testId}-average`} />
        <Stat label="Tertinggi" value={data.highest ?? "—"} />
        <Stat label="Terendah" value={data.lowest ?? "—"} />
        <Stat label="Jumlah Nilai" value={pts.length} />
        {t && (
          <span className={`inline-flex items-center gap-1.5 self-center px-3 py-1.5 rounded-full text-xs font-semibold ${t.cls}`} data-testid={`${testId}-trend`}>
            <t.Icon className="w-3.5 h-3.5" /> {t.label}
          </span>
        )}
      </div>
      {pts.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid={`${testId}-empty`}>{emptyText}</p>
      ) : (
        <div className="h-64 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 10, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="idx" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `#${v}`} />
              <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip content={<TrendTooltip />} />
              <ReferenceLine y={75} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "KKM 75", position: "insideTopRight", fontSize: 10, fill: "#b45309" }} />
              <Line type="monotone" dataKey="score" stroke="#0d9488" strokeWidth={3} dot={<Dot />} activeDot={{ r: 7 }} isAnimationActive />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="bg-slate-50 rounded-xl px-4 py-2 min-w-[90px]">
    <p className="text-[11px] text-slate-500">{label}</p>
    <p className="font-display text-xl font-bold text-slate-800" data-testid={testId}>{value}</p>
  </div>
);
